import { Configuration, IntentApi } from '@api/intent';
import { useIsAuthenticated } from '@interhyp/platform-auth';
import { getAPIPath } from '@interhyp/platform-request';
import { useContext as useRouterContext } from '@interhyp/platform-router';
import {
  IntentProvider as SharedIntentProvider,
  Intent,
  IntentContext,
  IntentInfo,
} from '@interhyp/shared-interim-portal';
import { useQuery } from '@tanstack/react-query';
import React, { PropsWithChildren, useMemo, useState } from 'react';

import { useInvalidateIntentsAfterActivation } from './hooks/useInvalidateIntentsAfterActivation';
import { IntentLoadingSpinner } from '../../components/IntentLoadingSpinner/IntentLoadingSpinner';

const service = new IntentApi(
  new Configuration({
    basePath: getAPIPath('intent'),
  }),
);

export function IntentProvider({ children }: PropsWithChildren<{}>): JSX.Element {
  const routerContext = useRouterContext();
  const intentId = routerContext?.id;
  const isAuthenticated = useIsAuthenticated();
  const [invalidateAfterActivation, setInvalidateAfterActivation] = useState<boolean>(false);

  const { data, isLoading } = useQuery({
    queryKey: ['intent', isAuthenticated, intentId ?? ''],
    retry: false,
    queryFn: async (): Promise<Intent | undefined> => {
      if (!isAuthenticated || !intentId) {
        return undefined;
      }

      const response = await service.getIntent(intentId);

      return response.data;
    },
  });

  const { waitForFetch, openActivationPage, reset } =
    useInvalidateIntentsAfterActivation(invalidateAfterActivation);

  const { data: intents } = useQuery({
    queryKey: ['intents', isAuthenticated],
    retry: false,
    queryFn: async (): Promise<IntentInfo[] | undefined> => {
      // Skip fetch if there is no authentication
      if (!isAuthenticated) {
        return undefined;
      }

      const response = await service.intentsOfAuthenticatedUser();

      // Reset activation page after data has been fetched
      reset();

      // At least one intent must be present otherwise reset / invalidation makes no sense
      setInvalidateAfterActivation(response.data?.length > 0);

      return response.data;
    },
  });

  const loadingStatus: IntentContext['loadingStatus'] = useMemo(() => {
    if (isLoading || openActivationPage) {
      return 'loading';
    }

    if (!intentId || !isAuthenticated) {
      return undefined;
    }

    return 'loaded';
  }, [isLoading, intentId, isAuthenticated, openActivationPage]);

  return (
    <SharedIntentProvider
      intent={data}
      loadingStatus={loadingStatus}
      intents={openActivationPage ? undefined : intents}
    >
      {waitForFetch && <IntentLoadingSpinner />}
      {children}
    </SharedIntentProvider>
  );
}
